import { type ReactElement, type ReactNode, useCallback, useState } from 'react';

import { AlertTriangle, ArrowRight, Check, CheckCircle2, CloudUpload, Copy, Eye, Loader2, Trash2 } from 'lucide-react';
import * as XLSX from 'xlsx';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field, FieldDescription, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

import { type StockEquivalencesResult, useUploadStockEquivalencesMutation } from '../../api/properties';
import type { Project } from '../../model/project';

type Step = 'setup' | 'review' | 'done';

const MAX_SIZE_MB = 20;
const MAX_CHANGES_SHOWN = 50;
const MAX_NOT_FOUND_SHOWN = 100;
const COPIED_FEEDBACK_MS = 2000;
// Mirrors the backend rule: the prefix becomes the "<prefix>:" label inside the SKU
const PREFIX_PATTERN = /^[A-Za-z0-9_]*$/;

interface ParsedFile {
  headers: string[];
  firstRow: Record<string, unknown>;
  rowCount: number;
}

interface ErrorResponse {
  data?: { message?: string };
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  masterProject: Pick<Project, 'id' | 'name'>;
}

const parseFile = async (file: File): Promise<ParsedFile> => {
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: 'array' });
  const sheet = workbook.Sheets[workbook.SheetNames[0]];
  const rows = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, { defval: '' });
  const firstRaw = rows[0] ?? {};
  // The backend trims headers before matching, so the column names sent must be trimmed too
  const firstRow = Object.fromEntries(Object.entries(firstRaw).map(([key, value]) => [key.trim(), value]));
  const headers = Object.keys(firstRow).filter((header) => header !== '' && !header.startsWith('__EMPTY'));
  return { headers, firstRow, rowCount: rows.length };
};

const sampleValue = (parsed: ParsedFile, column: string): string => String(parsed.firstRow[column] ?? '').trim();

const buildExampleSku = (stockId: string, code: string, prefix: string): string =>
  `${stockId || 'CAP7205249'} - ${prefix ? `${prefix}:` : ''}${code.replace(/\s+/g, '') || '58127044'}`;

interface ColumnSelectProps {
  id: string;
  label: string;
  description: ReactNode;
  value: string;
  onChange: (value: string) => void;
  parsed: ParsedFile;
  error?: string;
}

const ColumnSelect = ({ id, label, description, value, onChange, parsed, error }: ColumnSelectProps): ReactElement => (
  <Field data-invalid={!!error}>
    <FieldLabel htmlFor={id}>{label}</FieldLabel>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger id={id} className="w-full">
        <SelectValue placeholder="Selecciona una columna" />
      </SelectTrigger>
      <SelectContent>
        {parsed.headers.map((header) => (
          <SelectItem key={header} value={header}>
            {header}
            {sampleValue(parsed, header) && (
              <span className="text-text-subtle"> · ej. {sampleValue(parsed, header)}</span>
            )}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
    <FieldDescription>{description}</FieldDescription>
    {error && <FieldError>{error}</FieldError>}
  </Field>
);

interface StatProps {
  label: string;
  value: number;
  tone?: 'default' | 'warning';
}

// justify-between pins the value to the bottom, so values line up even when a label wraps
const Stat = ({ label, value, tone = 'default' }: StatProps): ReactElement => (
  <div className="flex h-full flex-col justify-between gap-1 rounded-lg border border-border-subtle bg-bg-surface-raised p-3">
    <span className="text-[10px] font-semibold uppercase tracking-wider text-text-subtle">{label}</span>
    <span
      className={
        tone === 'warning' && value > 0
          ? 'text-2xl font-extrabold font-display text-warning'
          : 'text-2xl font-extrabold font-display text-brand-navy'
      }
    >
      {value}
    </span>
  </div>
);

interface NotFoundListProps {
  codes: string[];
}

const NotFoundList = ({ codes }: NotFoundListProps): ReactElement => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (): Promise<void> => {
    try {
      // One per line, the full list: it usually ends up in a spreadsheet or a message to the client
      await navigator.clipboard.writeText(codes.join('\n'));
      setCopied(true);
      setTimeout(() => setCopied(false), COPIED_FEEDBACK_MS);
    } catch {
      // Clipboard might be unavailable (non-HTTPS contexts); the list can still be selected manually.
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-semibold text-brand-navy">Códigos sin propiedad ({codes.length})</p>
        <Button variant="ghost" size="sm" onClick={() => void handleCopy()}>
          {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
          {copied ? 'Copiado' : 'Copiar'}
        </Button>
      </div>
      <p className="text-xs text-text-subtle">
        Ningún SKU del proyecto contiene estos códigos. Suele pasar cuando la propiedad no está en Lidz o su código
        cambió en el CRM.
      </p>
      <div className="max-h-32 overflow-y-auto rounded-lg border border-border-subtle bg-bg-surface-raised p-3">
        <p className="text-xs text-text-muted break-words">
          {codes.slice(0, MAX_NOT_FOUND_SHOWN).join(', ')}
          {codes.length > MAX_NOT_FOUND_SHOWN && ` y ${codes.length - MAX_NOT_FOUND_SHOWN} más…`}
        </p>
      </div>
    </div>
  );
};

interface ResultViewProps {
  result: StockEquivalencesResult;
}

const ResultView = ({ result }: ResultViewProps): ReactElement => {
  const { summary, changes, notFound, invalidRows, tooLong } = result;
  return (
    <div className="flex flex-col gap-5">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat
          label={result.dryRun ? 'Propiedades a actualizar' : 'Propiedades actualizadas'}
          value={summary.propertiesUpdated}
        />
        <Stat label="Códigos encontrados" value={summary.stockIdsMatched} />
        <Stat label="Códigos no encontrados" value={summary.stockIdsNotFound} tone="warning" />
        <Stat label="Códigos ya presentes" value={summary.codesAlreadyPresent} />
      </div>

      {invalidRows.length > 0 && (
        <Alert variant="destructive">
          <AlertTriangle />
          <AlertTitle>{invalidRows.length} fila(s) ignoradas por datos vacíos</AlertTitle>
          <AlertDescription>
            <ul className="mt-1 max-h-28 overflow-y-auto list-disc pl-5 text-xs space-y-0.5">
              {invalidRows.slice(0, 25).map((row) => (
                <li key={row.row}>
                  Fila {row.row}: {row.reason}
                </li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      {tooLong.length > 0 && (
        <Alert variant="destructive">
          <AlertTriangle />
          <AlertTitle>{tooLong.length} propiedad(es) con códigos omitidos</AlertTitle>
          <AlertDescription>
            El SKU superaría los 255 caracteres. IDs: {tooLong.map((item) => item.propertyId).join(', ')}
          </AlertDescription>
        </Alert>
      )}

      {changes.length > 0 && (
        <div className="flex flex-col gap-2">
          <p className="text-sm font-semibold text-brand-navy">
            {result.dryRun ? 'Cambios a aplicar' : 'Cambios aplicados'} ({changes.length})
          </p>
          <div className="max-h-64 overflow-auto rounded-lg border border-border-subtle">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-bg-surface-raised">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold uppercase text-text-subtle tracking-wider">ID</th>
                  <th className="px-3 py-2 text-left font-semibold uppercase text-text-subtle tracking-wider">
                    SKU actual
                  </th>
                  <th className="px-3 py-2 text-left font-semibold uppercase text-text-subtle tracking-wider">
                    SKU nuevo
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border-subtle">
                {changes.slice(0, MAX_CHANGES_SHOWN).map((change) => (
                  <tr key={change.propertyId}>
                    <td className="px-3 py-2 text-text-muted">{change.propertyId}</td>
                    <td className="px-3 py-2 text-text-muted break-all">{change.from}</td>
                    <td className="px-3 py-2 font-medium text-brand-navy break-all">{change.to}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {changes.length > MAX_CHANGES_SHOWN && (
            <p className="text-[11px] text-text-subtle">Y {changes.length - MAX_CHANGES_SHOWN} cambios más…</p>
          )}
        </div>
      )}

      {notFound.length > 0 && <NotFoundList codes={notFound} />}
    </div>
  );
};

interface ReviewAlertProps {
  result: StockEquivalencesResult;
}

// A preview with nothing to write is a normal outcome (e.g. re-uploading the same file), so it gets its own message
const ReviewAlert = ({ result }: ReviewAlertProps): ReactElement => {
  const { changes, summary, tooLong } = result;
  if (changes.length > 0) {
    return (
      <Alert>
        <Eye />
        <AlertTitle>Vista previa: aún no se guardó nada</AlertTitle>
        <AlertDescription>Revisa los cambios y confirma para aplicarlos.</AlertDescription>
      </Alert>
    );
  }
  if (summary.stockIdsMatched === 0) {
    return (
      <Alert>
        <AlertTriangle />
        <AlertTitle>No se encontró ninguna propiedad</AlertTitle>
        <AlertDescription>
          Ningún SKU del proyecto contiene los códigos de la columna elegida. Revisa que sea la columna correcta.
        </AlertDescription>
      </Alert>
    );
  }
  if (tooLong.length === 0) {
    return (
      <Alert>
        <CheckCircle2 />
        <AlertTitle>Todo al día</AlertTitle>
        <AlertDescription>
          Las {summary.propertiesMatched} propiedades encontradas ya tienen sus códigos. No hay nada que aplicar.
        </AlertDescription>
      </Alert>
    );
  }
  return (
    <Alert>
      <AlertTriangle />
      <AlertTitle>No hay cambios que aplicar</AlertTitle>
      <AlertDescription>Revisa los avisos más abajo.</AlertDescription>
    </Alert>
  );
};

const StockEquivalencesDialog = ({ open, onOpenChange, masterProject }: Props): ReactElement => {
  const [step, setStep] = useState<Step>('setup');
  const [file, setFile] = useState<File | null>(null);
  const [parsed, setParsed] = useState<ParsedFile | null>(null);
  const [stockIdColumn, setStockIdColumn] = useState('');
  const [skuColumn, setSkuColumn] = useState('');
  const [skuPrefix, setSkuPrefix] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [parseError, setParseError] = useState<string | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);
  const [result, setResult] = useState<StockEquivalencesResult | null>(null);

  const [uploadEquivalences, { isLoading }] = useUploadStockEquivalencesMutation();

  const clearFile = useCallback(() => {
    setFile(null);
    setParsed(null);
    setStockIdColumn('');
    setSkuColumn('');
    setParseError(null);
    setServerError(null);
  }, []);

  const reset = useCallback(() => {
    clearFile();
    setStep('setup');
    setSkuPrefix('');
    setIsDragging(false);
    setResult(null);
  }, [clearFile]);

  const handleClose = (next: boolean): void => {
    if (!next) reset();
    onOpenChange(next);
  };

  const handleFile = async (selected: File): Promise<void> => {
    clearFile();
    if (selected.size > MAX_SIZE_MB * 1024 * 1024) {
      setParseError(`El archivo supera el tamaño máximo de ${MAX_SIZE_MB} MB.`);
      return;
    }
    if (!/\.(xls|xlsx|csv)$/i.test(selected.name)) {
      setParseError('Formato no soportado. Usa archivos .xlsx, .xls o .csv.');
      return;
    }
    try {
      const parsedFile = await parseFile(selected);
      if (parsedFile.headers.length < 2) {
        setParseError('El archivo necesita al menos dos columnas con encabezado en la primera fila.');
        return;
      }
      setFile(selected);
      setParsed(parsedFile);
    } catch (error) {
      setParseError(`No se pudo leer el archivo: ${error instanceof Error ? error.message : 'error desconocido'}`);
    }
  };

  const submit = async (dryRun: boolean): Promise<void> => {
    if (!file) return;
    setServerError(null);
    try {
      const response = await uploadEquivalences({
        projectId: masterProject.id,
        equivalencesFile: file,
        stockIdColumn,
        skuColumn,
        skuPrefix: skuPrefix.trim() || undefined,
        dryRun,
      }).unwrap();
      setResult(response);
      setStep(dryRun ? 'review' : 'done');
    } catch (err) {
      const { data } = err as ErrorResponse;
      setServerError(data?.message ?? 'Ocurrió un error al procesar el archivo.');
    }
  };

  const prefixInvalid = !PREFIX_PATTERN.test(skuPrefix.trim());
  const sameColumn = stockIdColumn !== '' && stockIdColumn === skuColumn;
  const canPreview = !!file && !!stockIdColumn && !!skuColumn && !sameColumn && !prefixInvalid;
  const example = parsed
    ? buildExampleSku(sampleValue(parsed, stockIdColumn), sampleValue(parsed, skuColumn), skuPrefix.trim())
    : null;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="!max-w-3xl !p-0 gap-0 overflow-hidden sm:!max-w-3xl w-[calc(100%-2rem)]">
        <DialogHeader className="text-left space-y-1 border-b border-border-subtle px-6 py-5 bg-bg-surface-raised">
          <DialogTitle className="font-display text-brand-navy text-xl leading-tight">
            Cargar tabla de equivalencias
          </DialogTitle>
          <DialogDescription className="text-xs">
            Agrega el código de un portal (Zonaprop, Mercado Libre, …) al SKU de cada propiedad de{' '}
            <span className="font-semibold">{masterProject.name}</span>, para que el agente la encuentre cuando un lead
            llegue solo con ese código. El SKU actual nunca se reemplaza: el código se agrega al final.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-5 p-6 overflow-y-auto max-h-[65vh] bg-bg-surface">
          {step === 'setup' && (
            <>
              {!file ? (
                <label
                  htmlFor="equivalences-file-input"
                  onDrop={(e) => {
                    e.preventDefault();
                    setIsDragging(false);
                    const dropped = e.dataTransfer.files?.[0];
                    if (dropped) void handleFile(dropped);
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setIsDragging(true);
                  }}
                  onDragLeave={(e) => {
                    e.preventDefault();
                    setIsDragging(false);
                  }}
                  className={`flex flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed p-8 cursor-pointer transition ${
                    isDragging
                      ? 'border-brand-vivid bg-brand-light/40'
                      : 'border-border-default bg-bg-surface-raised hover:border-brand-vivid hover:bg-brand-light/30'
                  }`}
                >
                  <span className="flex size-12 items-center justify-center rounded-full bg-brand-vivid text-text-inverse">
                    <CloudUpload className="size-6" />
                  </span>
                  <p className="text-base font-semibold text-brand-navy">
                    Arrastra la tabla de equivalencias o haz clic para subir
                  </p>
                  <p className="text-xs text-text-subtle">
                    Archivo exportado desde el portal · .xlsx, .xls o .csv · se lee solo la primera hoja
                  </p>
                  <input
                    id="equivalences-file-input"
                    type="file"
                    accept=".xls,.xlsx,.csv"
                    className="hidden"
                    onChange={(e) => {
                      const selected = e.target.files?.[0];
                      if (selected) void handleFile(selected);
                    }}
                  />
                </label>
              ) : (
                <div className="flex items-center justify-between rounded-xl border border-border-subtle bg-bg-surface-raised p-4">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="flex size-10 items-center justify-center rounded-full bg-brand-light text-brand-vivid">
                      <CloudUpload className="size-5" />
                    </span>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-brand-navy truncate">{file.name}</p>
                      <p className="text-xs text-text-subtle">
                        {parsed?.rowCount ?? 0} filas · {parsed?.headers.length ?? 0} columnas
                      </p>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" onClick={clearFile}>
                    <Trash2 className="size-4" />
                    Quitar
                  </Button>
                </div>
              )}

              {parseError && (
                <Alert variant="destructive">
                  <AlertTriangle />
                  <AlertTitle>No se pudo procesar el archivo</AlertTitle>
                  <AlertDescription>{parseError}</AlertDescription>
                </Alert>
              )}

              {parsed && (
                <FieldGroup>
                  <ColumnSelect
                    id="equivalences-stock-column"
                    label="Columna con el código de la propiedad en Lidz"
                    description="El código que ya está en el SKU de la propiedad, normalmente el código del CRM (ej. Tokko). Se busca dentro del SKU de todas las propiedades del proyecto."
                    value={stockIdColumn}
                    onChange={setStockIdColumn}
                    parsed={parsed}
                  />
                  <ColumnSelect
                    id="equivalences-sku-column"
                    label="Columna con el código del portal"
                    description="El código con el que el portal identifica la publicación. Es el que se agregará al SKU."
                    value={skuColumn}
                    onChange={setSkuColumn}
                    parsed={parsed}
                    error={sameColumn ? 'Debe ser una columna distinta a la del código en Lidz.' : undefined}
                  />
                  <Field data-invalid={prefixInvalid}>
                    <FieldLabel htmlFor="equivalences-prefix">Prefijo (opcional)</FieldLabel>
                    <Input
                      id="equivalences-prefix"
                      value={skuPrefix}
                      onChange={(e) => setSkuPrefix(e.target.value)}
                      placeholder="zonaprop, meli…"
                      maxLength={30}
                    />
                    <FieldDescription>
                      Nombre del portal que se escribe antes del código. Recomendado: permite distinguir portales y que
                      la sincronización con el CRM conserve el código.
                    </FieldDescription>
                    {prefixInvalid && <FieldError>Solo letras, números y &quot;_&quot;, sin espacios.</FieldError>}
                  </Field>
                </FieldGroup>
              )}

              {example && stockIdColumn && skuColumn && !sameColumn && (
                <div className="rounded-lg border border-border-subtle bg-bg-surface-raised p-4">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-text-subtle mb-1">
                    Así quedará el SKU (primera fila)
                  </p>
                  <p className="text-sm font-medium text-brand-navy break-all">{example}</p>
                </div>
              )}
            </>
          )}

          {step !== 'setup' && result && (
            <>
              {step === 'review' ? (
                <ReviewAlert result={result} />
              ) : (
                <Alert>
                  <CheckCircle2 />
                  <AlertTitle>Equivalencias aplicadas</AlertTitle>
                  <AlertDescription>
                    Se actualizaron {result.summary.propertiesUpdated} propiedades. Volver a cargar el mismo archivo no
                    duplica códigos.
                  </AlertDescription>
                </Alert>
              )}
              <ResultView result={result} />
            </>
          )}

          {serverError && (
            <Alert variant="destructive">
              <AlertTriangle />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{serverError}</AlertDescription>
            </Alert>
          )}
        </div>

        {/* mx-0/mb-0 undo DialogFooter's negative margins, which assume the default p-4 on DialogContent */}
        <DialogFooter className="mx-0 mb-0 border-t border-border-subtle px-6 pt-4 pb-2 sm:justify-between bg-bg-surface">
          {step === 'done' ? (
            <>
              <span />
              <Button type="button" onClick={() => handleClose(false)}>
                Listo
              </Button>
            </>
          ) : (
            <>
              <DialogClose asChild>
                <Button variant="ghost" type="button">
                  Cancelar
                </Button>
              </DialogClose>
              {step === 'setup' ? (
                <Button type="button" onClick={() => void submit(true)} disabled={!canPreview || isLoading}>
                  {isLoading ? <Loader2 className="size-4 animate-spin" /> : <Eye className="size-4" />}
                  {isLoading ? 'Analizando…' : 'Previsualizar cambios'}
                </Button>
              ) : (
                <div className="flex items-center gap-2">
                  <Button variant="outline" type="button" onClick={() => setStep('setup')} disabled={isLoading}>
                    Atrás
                  </Button>
                  {result?.changes.length === 0 ? (
                    <Button type="button" onClick={() => handleClose(false)}>
                      Listo
                    </Button>
                  ) : (
                    <Button type="button" onClick={() => void submit(false)} disabled={isLoading}>
                      {isLoading ? <Loader2 className="size-4 animate-spin" /> : <ArrowRight className="size-4" />}
                      {isLoading ? 'Aplicando…' : 'Confirmar y aplicar'}
                    </Button>
                  )}
                </div>
              )}
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default StockEquivalencesDialog;
